import * as React from 'react'

import apis from '@app/api/api'
import useI18n from '@store/i18n/useI18n'
import Logger from '@utils/Logger'
import theme from '@theme/Theme'
import { motion, container } from '@utils/Motion'

import styled from '@theme/styled-components'
import Searchbar from '@components/searchbar/searchbar'
import Loader from 'react-loader-spinner'

import * as JsSearch from 'js-search'
import AccentSanitizer from '../../../utils/AccentSanitizer'
import Swal from 'sweetalert2'
import ScreenContainer from '@components/screenContainer/screenContainer'
import InfiniteList from '@components/infiniteList/infiniteList'
import ExternalUser from '@components/listItem/externalUser'

import useReducer from '@store/useReducer'
import * as AccountStore from '@store/account/account'
import AdminTextValue from '@components/text/textArea'
interface Props {
  navigation: Navigation
}

const sanitizer = new AccentSanitizer()
const searchjs = new JsSearch.Search('id')

searchjs.searchIndex = new JsSearch.UnorderedSearchIndex()
searchjs.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
searchjs.tokenizer = {
  tokenize: (text: string) => text.split(' '),
}
searchjs.sanitizer = sanitizer
searchjs.addIndex('firstname')
searchjs.addIndex('lastname')
searchjs.addIndex('email')

const adminExternals = ({ navigation }: Props) => {
  const account = useReducer(AccountStore.store, (s) => s.account)
  const i18n = useI18n()
  const [screenStatus, setScreenStatus] = React.useState<'fetched' | 'loading' | 'error'>('loading')

  const [text, setText] = React.useState<string>('')
  const [adminUsers, setAdminUsers] = React.useState<AdminExternalUser[]>([])

  const [selectedUser, setSelectedUser] = React.useState<AdminExternalUser | undefined>(undefined)
  const [companyList, setCompanyList] = React.useState<Option[]>([])
  const [dirtyForm, setDirtyForm] = React.useState<boolean>(false)

  const isExternAdmin = !!account && account.role === 'extern_admin'

  React.useEffect(() => {
    fetchUsers()
  }, [])

  React.useEffect(() => {
    searchjs.addDocuments(adminUsers)
  }, [adminUsers, text])

  const fetchUsers = () => {
    if (isExternAdmin) {
      //On limite les administrateurs externes à l'édition des utilisateurs de la même entreprise
      return apis.admin.external
        .list()
        .then((fetchedUsers) => {
          setAdminUsers(fetchedUsers.externals)
          searchjs.addDocuments(fetchedUsers.externals)
          setScreenStatus('fetched')
        })
        .catch((err) => {
          Logger.info('ERROR FETCHING ADMIN USERS : ', err)
          setScreenStatus('error')
        })
    } else {
      return Promise.all([apis.admin.external.list(), apis.admin.list.companies()])
        .then(([fetchedUsers, fetchedCompanies]) => {
          setAdminUsers(fetchedUsers.externals)
          setCompanyList(fetchedCompanies.companies.map((c) => ({ label: c.libelle, value: c.id })))
          searchjs.addDocuments(fetchedUsers.externals)
          setScreenStatus('fetched')
        })
        .catch((err) => {
          Logger.info('ERROR FETCHING ADMIN USERS : ', err)
          setScreenStatus('error')
        })
    }
  }

  const alertCancellingEdition = (callback?: () => void) => {
    if (dirtyForm) {
      Swal.fire({
        title: i18n.t('components.adminExternals.alertCancelling.title'),
        text: i18n.t('components.adminExternals.alertCancelling.text'),
        showCancelButton: true,
        confirmButtonText: i18n.t('components.adminExternals.alertCancelling.confirm'),
        confirmButtonColor: 'green',
        cancelButtonText: i18n.t('components.adminExternals.alertCancelling.cancel'),
        cancelButtonColor: 'red',
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedUser(undefined)
          setDirtyForm(false)
          !!callback && callback()
        }
      })
    } else {
      setSelectedUser(undefined)
      setDirtyForm(false)
      !!callback && callback()
    }
  }

  const selectedCompany: Option | undefined = React.useMemo(
    () => (selectedUser && companyList.find((c) => c.value === selectedUser.companyId)) || undefined,
    [selectedUser]
  )

  const searchedUsers: AdminExternalUser[] = React.useMemo(
    () => (text && searchjs ? searchjs.search(sanitizer.sanitize(text.trim())) : adminUsers) as AdminExternalUser[],
    [adminUsers, text]
  )

  const sortedUsers: AdminExternalUser[] = React.useMemo(
    () => searchedUsers.sort((a, b) => a.firstname.localeCompare(b.firstname)),
    [searchedUsers]
  )

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.adminExternals.screenTitle')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={theme.colors.classicButtonLoader} height={30} />
            </StatusContainer>
          ) : screenStatus === 'error' ? (
            <ErrorLibelle>{i18n.t('components.adminExternals.error')}</ErrorLibelle>
          ) : (
            <>
              <Searchbar placeholder={i18n.t('components.adminExternals.searchbar')} text={text} setText={setText} />
              <UserList as={motion.div} className="container" variants={container} initial="hidden" animate="visible">
                {sortedUsers.length === 0 ? (
                  <NoItemContainer>
                    <NoItemLibelle>{i18n.t('components.adminExternals.noItem')}</NoItemLibelle>
                  </NoItemContainer>
                ) : (
                  <InfiniteList
                    data={sortedUsers}
                    height={100}
                    renderItem={(u) => (
                      <ExternalUser
                        user={u}
                        onClick={() =>
                          alertCancellingEdition(() => {
                            setSelectedUser(u)
                          })
                        }
                        height={100}
                      />
                    )}
                  />
                )}
              </UserList>
            </>
          )}
        </LeftContainer>
      }
      content={
        <RightContainer>
          {!!selectedUser ? (
            <FormContainer>
              <SectionTitleContainer>{i18n.t('components.adminExternals.section01')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminExternals.item01')}
                      value={selectedUser.firstname}
                      placeholder={i18n.t('common.placeholder.firstName')}
                    />
                  </FormItemContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminExternals.item02')}
                      value={selectedUser.lastname}
                      placeholder={i18n.t('common.placeholder.name')}
                    />
                  </FormItemContainer>
                </RowContainer>
                <RowContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminExternals.item03')}
                      value={selectedUser.email}
                      placeholder={i18n.t('common.placeholder.name')}
                    />
                  </FormItemContainer>
                </RowContainer>
                <RowContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminExternals.item06')}
                      value={selectedUser.idGrdf}
                      placeholder={i18n.t('common.placeholder.idGaia')}
                    />
                  </FormItemContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminExternals.item04')}
                      value={selectedUser.role}
                      placeholder={i18n.t('common.placeholder.role')}
                    />
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>

              {!isExternAdmin && (
                <>
                  <SectionTitleContainer>{i18n.t('components.adminExternals.section02')}</SectionTitleContainer>
                  <ColumnContainer>
                    <RowContainer>
                      <FormItemContainer>
                        <AdminTextValue
                          title={i18n.t('components.adminExternals.item05')}
                          value={selectedCompany?.label}
                          placeholder={i18n.t('common.placeholder.company')}
                        />
                      </FormItemContainer>
                    </RowContainer>
                  </ColumnContainer>
                </>
              )}
            </FormContainer>
          ) : (
            <NoSelectedContainer>
              <NoSelectedLibelle>{i18n.t('components.adminExternals.noSelected')}</NoSelectedLibelle>
            </NoSelectedContainer>
          )}
        </RightContainer>
      }
    />
  )
}

export default adminExternals

const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const UserList = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: scroll;
`
const NoItemContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoItemLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const RightContainer = styled('div')`
  flex: 1;
  display: flex;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const FormContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 50px;
  justify-content: flex-start;
  overflow: scroll;
`
const NoSelectedContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoSelectedLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const SectionTitleContainer = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 20px;
  line-height: 24px;
  color: #0053a1;
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  margin: 10px;
  margin-top: 0;
`
const ColumnContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')`
  display: flex;
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
